import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader, StatCard, Section } from "../../components/Common";
import { useAuth } from "../../lib/auth";
import { Link } from "react-router-dom";
import { ClipboardList, Wallet, Sparkles, MessageSquare, CalendarCheck } from "lucide-react";
import { PerformanceChart } from "../../components/Charts";
import { NoticeBoard } from "../../components/NoticeBoard";
import { formatCurrency } from "../../lib/currency";
import { useDataSync } from "../../lib/realtime";

export default function ParentOverview() {
  const { user } = useAuth();
  const [att, setAtt] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [fees, setFees] = useState([]);

  const load = async () => {
    try {
      const [a, t, f] = await Promise.all([
        api.get("/student/attendance"),
        api.get("/student/attempts"),
        api.get("/student/fees"),
      ]);
      setAtt(a.data); setAttempts(t.data); setFees(f.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);
  useDataSync(["fees", "attendance", "attempts"], () => load());

  const present = att.filter((a) => a.status === "present").length;
  const total = att.length;
  const presentPct = total ? Math.round((present / total) * 100) : 0;
  const avg = attempts.length ? (attempts.reduce((s, a) => s + a.percent, 0) / attempts.length).toFixed(1) : "—";
  const pending = fees.filter((f) => f.status !== "paid").reduce((s, f) => s + f.amount, 0);

  return (
    <div>
      <PageHeader title={`Hello, ${user.name.split(" ")[0]}`} subtitle="A snapshot of your child's progress" testId="parent-overview" />

      {!user.parent_of && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm">
          Your account isn't linked to a student yet. Please ask your institute admin to link your child.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Avg score" value={`${avg}${avg !== "—" ? "%" : ""}`} icon={ClipboardList} accent="mint" />
        <StatCard label="Attendance" value={`${presentPct}%`} sub={`${present}/${total}`} icon={CalendarCheck} />
        <StatCard label="Tests taken" value={attempts.length} icon={ClipboardList} accent="yellow" />
        <StatCard label="Fees due" value={formatCurrency(pending)} icon={Wallet} accent="coral" />
      </div>

      <div className="mt-6">
        <NoticeBoard />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <Section title="Performance trend">
          <PerformanceChart attempts={attempts} />
        </Section>
        <Section title="Recent test results">
          <div className="space-y-2">
            {attempts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                <div>
                  <div className="font-semibold text-slate-900">{a.test_title}</div>
                  <div className="text-xs text-slate-500">{new Date(a.submitted_at).toLocaleDateString()}</div>
                </div>
                <span className={`text-sm font-bold ${a.percent >= 70 ? "text-emerald-600" : a.percent >= 40 ? "text-amber-600" : "text-rose-600"}`}>{a.percent}%</span>
              </div>
            ))}
            {attempts.length === 0 && <p className="text-sm text-slate-500">No test results yet.</p>}
          </div>
        </Section>

        <Section title="Quick actions">
          <div className="grid grid-cols-2 gap-3">
            <Link to="/dashboard/parent/insights" className="p-4 rounded-lg bg-blue-50 hover:bg-blue-100">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <div className="mt-2 font-semibold text-slate-900">AI insights</div>
            </Link>
            <Link to="/dashboard/parent/chat" className="p-4 rounded-lg bg-emerald-50 hover:bg-emerald-100">
              <MessageSquare className="h-5 w-5 text-emerald-600" />
              <div className="mt-2 font-semibold text-slate-900">Chat teacher</div>
            </Link>
          </div>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="Fees">
          <div className="divide-y divide-slate-100">
            {fees.map((f) => (
              <div key={f.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold text-slate-900">{formatCurrency(f.amount, f.currency)}</div>
                  <div className="text-xs text-slate-500">Due {f.due_date} · {f.description || "Tuition"}</div>
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  f.status === "paid" ? "bg-emerald-50 text-emerald-700" :
                  f.status === "overdue" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
                }`}>{f.status}</span>
              </div>
            ))}
            {fees.length === 0 && <p className="text-sm text-slate-500 py-4">No fees on record.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}
